import React from 'react';

import { Icon, Text } from '@betterment/samba-react';
import { Flash, theme } from '@betterment/style-closet-react';
import styled from '@emotion/styled';

import { CHECKING_SUPPORT_PHONE, OPS_LOCKED_EMAIL } from '../../Shared/constants';

const SupportLink = styled.a`
  text-decoration: underline;
  color: ${theme.colors.bmtBlue} !important;
`;

interface Props {
  message: string;
}

const LockIcon = () => <Icon name="Lock" size="small" />;

const RootBannerWithPhoneLink: React.FC<React.PropsWithChildren<Props>> = ({ message }) => {
  return (
    <Flash variant="error" icon={LockIcon} mt="0">
      <Text>
        {message} Please call us at {CHECKING_SUPPORT_PHONE} during{' '}
        <SupportLink href="https://www.betterment.com/contact/" target="_blank" rel="noopener noreferrer">
          support hours
        </SupportLink>{' '}
        to resolve.
      </Text>
    </Flash>
  );
};

const RootBannerWithEmailLink: React.FC<React.PropsWithChildren<Props>> = ({ message }) => {
  return (
    <Flash variant="error" icon={LockIcon} mt="0">
      <Text>
        {message} Please contact us at <SupportLink href={`mailto:${OPS_LOCKED_EMAIL}`}>{OPS_LOCKED_EMAIL}</SupportLink>{' '}
        to resolve.
      </Text>
    </Flash>
  );
};

enum LockedState {
  Customer,
  Account,
  DebitCard,
  None,
}

function assertUnreachable(): never {
  throw new Error('Unreachable code');
}

const getLockedState = (accountLocked: boolean, cardLocked: boolean): LockedState => {
  if (accountLocked) {
    if (cardLocked) {
      return LockedState.Customer;
    }
    return LockedState.Account;
  }
  if (cardLocked) {
    return LockedState.DebitCard;
  }
  return LockedState.None;
};

export interface LockedBannerProps {
  accountLocked: boolean;
  cardLocked: boolean;
}

export const LockedBanner: React.FC<React.PropsWithChildren<LockedBannerProps>> = ({ accountLocked, cardLocked }) => {
  const lockedState = getLockedState(accountLocked, cardLocked);
  switch (lockedState) {
    case LockedState.Account:
      return <RootBannerWithEmailLink message="Account locked." />;
    case LockedState.Customer:
      return <RootBannerWithPhoneLink message="We have locked your Checking account." />;
    case LockedState.DebitCard:
      return <RootBannerWithPhoneLink message="We have locked your debit card due to suspicious activity." />;
    case LockedState.None:
      return null;
  }
  return assertUnreachable();
};
